"use client";

import React, { useState, useEffect, useRef } from "react";
import { ChevronRight } from "lucide-react";

export type AnimationMode = "typewriter" | "slide" | "instant";
export type EasingMode = "accelerating" | "linear" | "smooth";
export type VisualVariant = "subtle" | "badge";

export interface TokenNode {
  type: "text" | "explainable";
  id?: string;
  term?: string;
  explanation?: string;
  children?: TokenNode[];
}

/**
 * Parses markdown-like string with arbitrary levels of nested `[term](explanation)` markup.
 */
export function parseExplainableTokens(text: string, pathPrefix = "0"): TokenNode[] {
  const nodes: TokenNode[] = [];
  let i = 0;
  let textBuffer = "";

  while (i < text.length) {
    if (text[i] === "[") {
      // Find matching ']'
      let depth = 1;
      let j = i + 1;
      while (j < text.length && depth > 0) {
        if (text[j] === "[") depth++;
        else if (text[j] === "]") depth--;
        j++;
      }

      // Check if immediately followed by '('
      if (depth === 0 && j < text.length && text[j] === "(") {
        const termRaw = text.slice(i + 1, j - 1);
        let k = j + 1;
        let parenDepth = 1;
        while (k < text.length && parenDepth > 0) {
          if (text[k] === "(") parenDepth++;
          else if (text[k] === ")") parenDepth--;
          k++;
        }

        if (parenDepth === 0) {
          if (textBuffer) {
            nodes.push({ type: "text", term: textBuffer });
            textBuffer = "";
          }

          const explanationRaw = text.slice(j + 1, k - 1);
          const tokenId = `${pathPrefix}-${nodes.length}-${termRaw
            .toLowerCase()
            .replace(/[^a-zA-Z0-9]/g, "")
            .slice(0, 18)}`;

          nodes.push({
            type: "explainable",
            id: tokenId,
            term: termRaw,
            explanation: explanationRaw,
            children: parseExplainableTokens(explanationRaw, `${tokenId}-c`),
          });

          i = k;
          continue;
        }
      }
    }

    textBuffer += text[i];
    i++;
  }

  if (textBuffer) {
    nodes.push({ type: "text", term: textBuffer });
  }

  return nodes;
}

/**
 * Computes step delay (in ms) for non-linear typing easing.
 */
function getStepDelay(
  step: number,
  totalLength: number,
  baseSpeed: number,
  easing: EasingMode
): number {
  if (easing === "linear") {
    return Math.max(4, baseSpeed);
  }

  const progress = Math.min(1, Math.max(0, step / Math.max(1, totalLength)));

  if (easing === "accelerating") {
    // Starts deliberate (~32ms) and rapidly accelerates down to 4ms
    const startDelay = Math.max(26, baseSpeed * 2);
    const minDelay = 4;
    return Math.max(minDelay, Math.round(startDelay - Math.pow(progress, 0.5) * (startDelay - minDelay)));
  }

  // Smooth S-Curve: gentle start, fast middle, gentle end
  const bell = Math.sin(progress * Math.PI);
  const slowDelay = Math.max(24, baseSpeed * 1.8);
  const fastDelay = 4;
  return Math.max(fastDelay, Math.round(slowDelay - bell * (slowDelay - fastDelay)));
}

export interface ExplainableInlineProps {
  id?: string;
  term: string;
  explanation: string;
  childrenNodes?: TokenNode[];
  mode?: AnimationMode;
  easing?: EasingMode;
  variant?: VisualVariant;
  speedMs?: number;
  showCursor?: boolean;
  prefix?: string;
  suffix?: string;
  isExpanded?: boolean;
  expandedIds?: string[];
  onToggle?: (id: string, next: boolean) => void;
  className?: string;
}

export function ExplainableInline({
  id,
  term,
  explanation,
  childrenNodes = [],
  mode = "typewriter",
  easing = "accelerating",
  variant = "subtle",
  speedMs = 14,
  showCursor = true,
  prefix = " (",
  suffix = ")",
  isExpanded = false,
  expandedIds = [],
  onToggle,
  className = "",
}: ExplainableInlineProps) {
  const componentId =
    id ||
    `exp-${term
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "-")
      .replace(/-+/g, "-")
      .slice(0, 30)}`;

  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = onToggle !== undefined;
  const effectiveExpanded = isControlled ? isExpanded : internalExpanded;

  // Plain text representation for typewriter progress & crawler pre-rendering
  const plainExplanation = `${prefix}${explanation.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")}${suffix}`;
  const [displayedLength, setDisplayedLength] = useState(effectiveExpanded ? plainExplanation.length : 0);
  const [isTyping, setIsTyping] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!effectiveExpanded) {
      setDisplayedLength(0);
      setIsTyping(false);
      return;
    }

    if (mode === "instant" || mode === "slide") {
      setDisplayedLength(plainExplanation.length);
      setIsTyping(false);
      return;
    }

    // Typewriter with dynamic non-linear acceleration
    setDisplayedLength(0);
    setIsTyping(true);
    let cur = 0;

    const scheduleNext = () => {
      cur += 1;
      setDisplayedLength(cur);

      if (cur < plainExplanation.length) {
        const nextDelay = getStepDelay(cur, plainExplanation.length, speedMs, easing);
        timeoutRef.current = setTimeout(scheduleNext, nextDelay);
      } else {
        timeoutRef.current = null;
        setTimeout(() => setIsTyping(false), 200);
      }
    };

    const initialDelay = getStepDelay(0, plainExplanation.length, speedMs, easing);
    timeoutRef.current = setTimeout(scheduleNext, initialDelay);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [effectiveExpanded, plainExplanation, mode, speedMs, easing]);

  const handleToggle = () => {
    if (isControlled) {
      onToggle(componentId, !isExpanded);
    } else {
      setInternalExpanded((prev) => !prev);
    }
  };

  // Clean trigger button styling without clashing borders
  const triggerButtonStyles =
    variant === "subtle"
      ? `group relative inline-flex items-center gap-1 align-baseline cursor-pointer font-bold transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-1 select-none mx-0.5 px-0.5 ${
          effectiveExpanded
            ? "text-rose-700 dark:text-rose-300"
            : "text-neutral-900 hover:text-rose-600 dark:text-neutral-100 dark:hover:text-rose-300"
        }`
      : `group relative inline-flex items-center gap-1 align-baseline cursor-pointer font-bold rounded-md px-1.5 py-0.5 mx-0.5 transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-1 select-none border ${
          effectiveExpanded
            ? "bg-rose-500/10 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200 border-rose-500/30"
            : "bg-rose-500/10 text-rose-700 hover:bg-rose-500/20 dark:bg-rose-500/15 dark:text-rose-300 dark:hover:bg-rose-500/25 border-rose-500/20 hover:border-rose-500/40"
        }`;

  const hasNestedChildren = childrenNodes.some((c) => c.type === "explainable");
  const isFullyTyped = displayedLength >= plainExplanation.length;

  return (
    <span className={`inline ${className}`}>
      {/* Trigger Button: Chevron points right when closed, points LEFT (rotate-180) when open */}
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={effectiveExpanded}
        aria-controls={componentId}
        aria-label={`${term}: ${effectiveExpanded ? "Erklärung einklappen (nach links)" : "Erklärung im Lesefluss entfalten (nach rechts)"}`}
        className={triggerButtonStyles}
      >
        <span>{term}</span>
        <ChevronRight
          className={`h-3 w-3 shrink-0 text-rose-500 transition-transform duration-250 ${
            effectiveExpanded ? "rotate-180 text-rose-600 dark:text-rose-400" : "group-hover:translate-x-0.5"
          }`}
          aria-hidden="true"
        />
      </button>

      {/* SEO & DOM Crawler Anchor: Pre-rendered in SSR/SSG so Googlebot & parsers index the content */}
      {!effectiveExpanded && (
        <span
          id={componentId}
          className="hidden"
          aria-hidden="true"
          data-seo-content="true"
        >
          {plainExplanation}
        </span>
      )}

      {/* Expanded Inline Explanation: Clean, natural sentence flow without misleading red underline/box */}
      {effectiveExpanded && (
        <span
          id={componentId}
          className="inline text-neutral-750 dark:text-neutral-250 font-normal"
        >
          <span className="text-neutral-400 dark:text-neutral-500 font-normal">{prefix}</span>

          {/* If typing in typewriter mode and not finished, stream the plain text */}
          {mode === "typewriter" && !isFullyTyped ? (
            <span>
              {plainExplanation.slice(prefix.length, Math.max(prefix.length, displayedLength - suffix.length))}
            </span>
          ) : (
            /* Once typed (or in slide/instant), render the full rich content including nested explainables */
            <span>
              {hasNestedChildren ? (
                <ExplainableNodeList
                  nodes={childrenNodes}
                  mode={mode}
                  easing={easing}
                  variant={variant}
                  speedMs={speedMs}
                  showCursor={showCursor}
                  expandedIds={expandedIds}
                  onToggleId={onToggle}
                />
              ) : (
                <span>{explanation}</span>
              )}
            </span>
          )}

          <span className="text-neutral-400 dark:text-neutral-500 font-normal">{suffix}</span>

          {/* Fine subtle typing caret */}
          {isTyping && showCursor && (
            <span
              className="inline-block h-3.5 w-[1.5px] bg-rose-500 animate-pulse ml-0.5 align-middle"
              aria-hidden="true"
            />
          )}
        </span>
      )}
    </span>
  );
}

/**
 * Renders an array of TokenNodes recursively.
 */
export function ExplainableNodeList({
  nodes,
  mode = "typewriter",
  easing = "accelerating",
  variant = "subtle",
  speedMs = 14,
  showCursor = true,
  expandedIds = [],
  onToggleId,
}: {
  nodes: TokenNode[];
  mode?: AnimationMode;
  easing?: EasingMode;
  variant?: VisualVariant;
  speedMs?: number;
  showCursor?: boolean;
  expandedIds?: string[];
  onToggleId?: (id: string, next: boolean) => void;
}) {
  return (
    <>
      {nodes.map((node, index) => {
        if (node.type === "text") {
          return <span key={`text-${index}`}>{node.term}</span>;
        }

        const isExpanded = node.id ? expandedIds.includes(node.id) : false;

        return (
          <ExplainableInline
            key={node.id || `node-${index}`}
            id={node.id || `node-${index}`}
            term={node.term || ""}
            explanation={node.explanation || ""}
            childrenNodes={node.children || []}
            mode={mode}
            easing={easing}
            variant={variant}
            speedMs={speedMs}
            showCursor={showCursor}
            isExpanded={isExpanded}
            expandedIds={expandedIds}
            onToggle={onToggleId}
          />
        );
      })}
    </>
  );
}

/**
 * Top-level ExplainableText container: parses text with nested tokens and renders them.
 */
export function ExplainableText({
  text,
  mode = "typewriter",
  easing = "accelerating",
  variant = "subtle",
  speedMs = 14,
  showCursor = true,
  expandedIds = [],
  onToggleId,
}: {
  text: string;
  mode?: AnimationMode;
  easing?: EasingMode;
  variant?: VisualVariant;
  speedMs?: number;
  showCursor?: boolean;
  expandedIds?: string[];
  onToggleId?: (id: string, next: boolean) => void;
}) {
  const parsedNodes = React.useMemo(() => parseExplainableTokens(text), [text]);

  return (
    <span className="leading-relaxed">
      <ExplainableNodeList
        nodes={parsedNodes}
        mode={mode}
        easing={easing}
        variant={variant}
        speedMs={speedMs}
        showCursor={showCursor}
        expandedIds={expandedIds}
        onToggleId={onToggleId}
      />
    </span>
  );
}
